import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchSingleCompanyDetails } from '../../../store/investor/action';
import { getGoogleFaviconUrl, getFaviconUrl, isValidWebsiteUrl } from '../../../utils/urlMetadata';

// Ordered logo-source cache. Key: `${investorOrgId}:${assesseeOrgId}` so a logo
// resolved for one investor org is never served to a different org that logs in
// on the same tab (the module stays resident across SPA logout/login).
const logoCache = new Map<string, string[]>();
// Subscribers waiting for a pending fetch, keyed the same way as logoCache.
const subscribers = new Map<string, Set<(sources: string[]) => void>>();

const EMPTY: string[] = [];

// Ordered fallback chain, same pattern as KeyCompetitors' CompetitorLogo:
// real logo → Google favicon → site's own favicon. The card tries each in turn
// (advancing on <img> error) before dropping to the initials placeholder.
function resolveSources(logo?: string, websiteUrl?: string): string[] {
    const sources: string[] = [];
    if (logo) sources.push(logo);
    const site = websiteUrl?.trim();
    if (site && isValidWebsiteUrl(site)) {
        const google = getGoogleFaviconUrl(site, 128);
        if (google) sources.push(google);
        const favicon = getFaviconUrl(site);
        if (favicon) sources.push(favicon);
    }
    return sources;
}

/**
 * Lazy-fetches a company's logo sources using the same API the AICA report uses.
 * Returns an ordered list of candidate image URLs (logo → Google favicon → site
 * favicon); the caller renders them with an error-fallback chain. Caches results
 * so each company is fetched only once per session, and multiple cards for the
 * same company all receive the sources when the single request completes.
 */
export function useCompanyLogo(assesseeOrgId: string | undefined): string[] {
    const dispatch = useAppDispatch();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const cacheKey = assesseeOrgId && investorOrgId ? `${investorOrgId}:${assesseeOrgId}` : '';

    const [sources, setSources] = useState<string[]>(() => (cacheKey && logoCache.has(cacheKey) ? logoCache.get(cacheKey)! : EMPTY));

    useEffect(() => {
        if (!cacheKey) return;

        // Already resolved — use cached value
        if (logoCache.has(cacheKey)) {
            setSources(logoCache.get(cacheKey)!);
            return;
        }

        // A request is already in-flight — subscribe and wait
        if (subscribers.has(cacheKey)) {
            const subs = subscribers.get(cacheKey)!;
            subs.add(setSources);
            return () => {
                subs.delete(setSources);
            };
        }

        // First request for this company — create subscriber set and fetch
        const subs = new Set<(sources: string[]) => void>();
        subs.add(setSources);
        subscribers.set(cacheKey, subs);

        dispatch(
            fetchSingleCompanyDetails(
                { assesseeOrgId: assesseeOrgId!, investorOrgId },
                {
                    onSuccess: (companyData: any) => {
                        const resolved = resolveSources(companyData?.logo, companyData?.websiteUrl);
                        logoCache.set(cacheKey, resolved);
                        subscribers.get(cacheKey)?.forEach((cb) => cb(resolved));
                        subscribers.delete(cacheKey);
                    },
                    onError: () => {
                        logoCache.set(cacheKey, EMPTY);
                        subscribers.get(cacheKey)?.forEach((cb) => cb(EMPTY));
                        subscribers.delete(cacheKey);
                    },
                },
            ),
        );

        return () => {
            subs.delete(setSources);
        };
    }, [cacheKey, assesseeOrgId, investorOrgId, dispatch]);

    return sources;
}
